import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getManagerDashboard, type DashboardResponse } from "../../services/dashboard-api";
import { DashboardCharts } from "../../components/dashboard/DashboardCharts";
import { ResultsTable } from "../../components/dashboard/ResultsTable";
import { Ranking } from "../../components/dashboard/Ranking";
import { DepartmentFilter } from "../../components/dashboard/DepartmentFilter";

/** FR-017/FR-033–FR-035: the Manager's own dashboard — charts, results table, and ranking,
 * scoped strictly to their own company by the session (no id in the URL), filterable by
 * department. */
export function ManagerDashboardPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [allDepartments, setAllDepartments] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);

  useEffect(() => {
    getManagerDashboard([]).then((d) => {
      setAllDepartments(
        Array.from(new Set(d.table.map((r) => r.department).filter((d): d is string => !!d))),
      );
    });
  }, []);

  useEffect(() => {
    getManagerDashboard(selected).then(setDashboard);
  }, [selected]);

  if (!dashboard) return <div className="p-6 text-body">Loading…</div>;

  return (
    <div className="p-6">
      <h1 className="mb-4 font-sans text-xl text-ink">{t("dashboard.title", "Report")}</h1>
      <DepartmentFilter options={allDepartments} selected={selected} onChange={setSelected} />

      <div className="mb-8">
        <DashboardCharts charts={dashboard.charts} />
      </div>

      {/* FR-075: ranking sits above the results table. */}
      <div className="mb-8">
        <h2 className="mb-2 font-sans text-lg text-ink">{t("dashboard.ranking", "Ranking")}</h2>
        <Ranking entries={dashboard.ranking} />
      </div>

      <div>
        <h2 className="mb-2 font-sans text-lg text-ink">{t("dashboard.results", "Results")}</h2>
        <ResultsTable
          rows={dashboard.table}
          onSelectMember={(memberId) => navigate(`/company/${slug}/dashboard/members/${memberId}`)}
        />
      </div>
    </div>
  );
}
